import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { LANGUAGES, type Lang, type MessageKey } from "./strings";
import { safeStorage } from "../lib/storage";
import { useBrand } from "../branding/BrandContext";

type Vars = Record<string, string | number>;

export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
}

/**
 * Language for this visit, first match wins: `?lang=`, the visitor's saved choice, the browser's
 * languages, then the operator's default. Only languages the operator enabled are considered.
 */
export function detectLang(
  enabled: readonly Lang[],
  fallback: Lang,
  env: { search: string; stored: string | null; languages: readonly string[] },
): Lang {
  const ok = (l: string | null | undefined): l is Lang => !!l && l in LANGUAGES && enabled.includes(l as Lang);
  const fromUrl = new URLSearchParams(env.search).get("lang");
  if (ok(fromUrl)) return fromUrl;
  if (ok(env.stored)) return env.stored;
  for (const l of env.languages) {
    const short = l.slice(0, 2).toLowerCase();
    if (ok(short)) return short;
  }
  return ok(fallback) ? fallback : (enabled[0] ?? "en");
}

function browserEnv() {
  return {
    search: window.location.search,
    stored: safeStorage.get("lang"),
    languages: navigator.languages?.length ? navigator.languages : [navigator.language ?? "en"],
  };
}

interface I18n {
  lang: Lang;
  /** Languages the operator enabled, in display order. */
  languages: readonly Lang[];
  /** BCP-47 locale for number and date formatting. */
  locale: string;
  setLang(lang: Lang): void;
  t(key: MessageKey, vars?: Vars): string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Lang }) {
  const brand = useBrand();
  const enabled = brand.checkout.enabledLocales;
  const fallback = brand.checkout.defaultLocale;
  const [lang, setLangState] = useState<Lang>(() => initial ?? detectLang(enabled, fallback, browserEnv()));

  // The operator can switch a language off while a visitor has the page open (config revalidation).
  useEffect(() => {
    if (!initial && !enabled.includes(lang)) setLangState(detectLang(enabled, fallback, browserEnv()));
  }, [enabled, fallback, lang, initial]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    safeStorage.set("lang", next);
  }, []);

  const value = useMemo<I18n>(() => {
    const messages = LANGUAGES[lang];
    return {
      lang,
      languages: enabled,
      locale: lang === "sk" ? "sk-SK" : "en-US",
      setLang,
      t: (key, vars) => interpolate(messages[key] ?? LANGUAGES.en[key] ?? key, vars),
    };
  }, [lang, setLang, enabled]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n outside <I18nProvider>");
  return ctx;
}
